import React from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { AvatarUpload } from './AvatarUpload';
import MediaItemsField from './MediaItemsField';
import IconPicker from './IconPicker';
import { Plus, X, ExternalLink } from 'lucide-react';

const FieldEditor = ({ field, value, onChange }) => {
  const renderField = () => {
    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            maxLength={field.max_length}
          />
        );

      case 'textarea':
      case 'richtext':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={field.field_type === 'richtext' ? 6 : 3}
            className="resize-none"
          />
        );

      case 'url':
        return (
          <div className="flex gap-2">
            <Input
              value={value || ''}
              onChange={(e) => onChange(field.name, e.target.value)}
              placeholder="https://..."
              type="url"
            />
            {value && (
              <a href={value} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-500 hover:text-blue-600">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        );

      case 'image':
        return (
          <AvatarUpload
            value={value || ''}
            onChange={(url) => onChange(field.name, url)}
            folder={field.folder || 'general'}
            shape={field.shape || 'rect'}
            testId={`field-image-${field.name}`}
          />
        );

      case 'cta': {
        const ctaValue = value || { label: '', url: '', style: 'primary' };
        return (
          <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-xs">Button Label</Label>
              <Input
                value={ctaValue.label || ''}
                onChange={(e) => onChange(field.name, { ...ctaValue, label: e.target.value })}
                placeholder="Click here"
              />
            </div>
            <div>
              <Label className="text-xs">URL</Label>
              <Input
                value={ctaValue.url || ''}
                onChange={(e) => onChange(field.name, { ...ctaValue, url: e.target.value })}
                placeholder="#section or /page"
              />
            </div>
            <div>
              <Label className="text-xs">Style</Label>
              <select
                value={ctaValue.style || 'primary'}
                onChange={(e) => onChange(field.name, { ...ctaValue, style: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="primary">Primary (Gold)</option>
                <option value="secondary">Secondary</option>
                <option value="outline">Outline</option>
              </select>
            </div>
          </div>
        );
      }

      case 'boolean':
        return (
          <Switch
            checked={value || false}
            onCheckedChange={(checked) => onChange(field.name, checked)}
          />
        );

      case 'media_items':
        return (
          <MediaItemsField
            value={value || []}
            onChange={(next) => onChange(field.name, next)}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(field.name, parseInt(e.target.value) || 0)}
          />
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
          >
            <option value="">Select...</option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'background': {
        const bg = value && typeof value === 'object' ? value : { type: 'none' };
        const setBg = (patch) => onChange(field.name, { ...bg, ...patch });
        return (
          <div className="space-y-3 p-3 border rounded-lg bg-slate-50">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'none', label: 'Default' },
                { key: 'color', label: 'Solid Color' },
                { key: 'image', label: 'Image' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setBg({ type: opt.key })}
                  className={`px-3 py-1.5 rounded-md text-sm border ${
                    bg.type === opt.key
                      ? 'bg-[#00457f] text-white border-[#00457f]'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {bg.type === 'color' && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-600 w-24">Color</label>
                <input type="color" value={bg.color || '#0a1a2f'} onChange={(e) => setBg({ color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                <Input value={bg.color || ''} onChange={(e) => setBg({ color: e.target.value })} placeholder="#0a1a2f" className="text-sm max-w-[140px]" />
                <label className="ml-auto flex items-center gap-1 text-xs text-slate-600">
                  <input type="checkbox" checked={bg.dark_text === true} onChange={(e) => setBg({ dark_text: e.target.checked })} />
                  Dark text
                </label>
              </div>
            )}
            {bg.type === 'image' && (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Image URL</label>
                  <Input value={bg.image || ''} onChange={(e) => setBg({ image: e.target.value })} placeholder="https://..." className="text-sm" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs text-slate-600 w-24">Dark overlay</label>
                  <input type="range" min="0" max="80" step="5" value={bg.overlay ?? 40} onChange={(e) => setBg({ overlay: parseInt(e.target.value, 10) })} className="flex-1" />
                  <span className="text-xs text-slate-600 w-8 text-right">{bg.overlay ?? 40}%</span>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'boxes': {
        const boxes = Array.isArray(value) ? value : [];
        const updateBox = (idx, patch) => {
          const next = boxes.map((b, i) => (i === idx ? { ...b, ...patch } : b));
          onChange(field.name, next);
        };
        const removeBox = (idx) => onChange(field.name, boxes.filter((_, i) => i !== idx));
        const moveBox = (idx, dir) => {
          const j = idx + dir;
          if (j < 0 || j >= boxes.length) return;
          const next = [...boxes];
          [next[idx], next[j]] = [next[j], next[idx]];
          onChange(field.name, next);
        };
        return (
          <div className="space-y-3">
            {boxes.map((box, idx) => (
              <div key={idx} className="border rounded-lg p-3 bg-white space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Box {idx + 1}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveBox(idx, -1)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Move up">↑</button>
                    <button type="button" onClick={() => moveBox(idx, 1)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Move down">↓</button>
                    <button type="button" onClick={() => removeBox(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="md:col-span-1">
                    <IconPicker value={box.icon || ''} onChange={(name) => updateBox(idx, { icon: name })} testId={`boxes-icon-${idx}`} />
                  </div>
                  <Input value={box.title || ''} onChange={(e) => updateBox(idx, { title: e.target.value })} placeholder="Title" className="text-sm md:col-span-2" />
                </div>
                <textarea value={box.description || ''} onChange={(e) => updateBox(idx, { description: e.target.value })} placeholder="Short description" rows={2} className="w-full text-sm px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#00457f]" />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => onChange(field.name, [...boxes, { icon: '', title: '', description: '' }])}>
              <Plus className="w-4 h-4 mr-1" /> Add Box
            </Button>
          </div>
        );
      }

      case 'sponsor_repeater': {
        const items = Array.isArray(value) ? value : [];
        const tierOptions = ['platinum', 'gold', 'silver', 'standard'];
        const updateItem = (idx, patch) => {
          const next = [...items];
          next[idx] = { ...next[idx], ...patch };
          onChange(field.name, next);
        };
        const removeItem = (idx) => onChange(field.name, items.filter((_, i) => i !== idx));
        const moveItem = (idx, dir) => {
          const target = idx + dir;
          if (target < 0 || target >= items.length) return;
          const next = [...items];
          [next[idx], next[target]] = [next[target], next[idx]];
          onChange(field.name, next);
        };
        const addItem = () => onChange(field.name, [...items, { name: '', tier: 'standard', logo_url: '', website_url: '' }]);

        return (
          <div className="space-y-3">
            {items.length === 0 && (
              <p className="text-xs text-slate-400 italic px-2 py-3 border border-dashed rounded-md text-center">
                No sponsors yet. Click "Add Sponsor" to upload a logo.
              </p>
            )}
            {items.map((item, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Sponsor #{idx + 1}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveItem(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                      data-testid={`sponsor-move-up-${idx}`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(idx, 1)}
                      disabled={idx === items.length - 1}
                      className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                      data-testid={`sponsor-move-down-${idx}`}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      title="Remove sponsor"
                      data-testid={`sponsor-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Logo</Label>
                    <AvatarUpload
                      value={item.logo_url || ''}
                      onChange={(url) => updateItem(idx, { logo_url: url })}
                      folder="sponsors"
                      shape="rect"
                      previewSize={72}
                      hint="PNG / SVG / JPG · transparent bg recommended · up to 100 MB"
                      testId={`sponsor-logo-upload-${idx}`}
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Sponsor Name</Label>
                    <Input
                      value={item.name || ''}
                      onChange={(e) => updateItem(idx, { name: e.target.value })}
                      placeholder="Company Inc."
                      data-testid={`sponsor-name-${idx}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Tier</Label>
                      <select
                        value={item.tier || 'standard'}
                        onChange={(e) => updateItem(idx, { tier: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-[#00457f] focus:border-transparent"
                        data-testid={`sponsor-tier-${idx}`}
                      >
                        {tierOptions.map(t => (
                          <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Website URL</Label>
                      <Input
                        value={item.website_url || ''}
                        onChange={(e) => updateItem(idx, { website_url: e.target.value })}
                        placeholder="https://example.com"
                        type="url"
                        data-testid={`sponsor-website-${idx}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={addItem}
              data-testid="sponsor-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Sponsor
            </Button>
          </div>
        );
      }

      case 'repeater': {
        const items = value || [];
        return (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className="flex gap-2 p-2 bg-gray-50 rounded">
                <Input
                  value={typeof item === 'string' ? item : JSON.stringify(item)}
                  onChange={(e) => {
                    const newItems = [...items];
                    try { newItems[idx] = JSON.parse(e.target.value); } catch { newItems[idx] = e.target.value; }
                    onChange(field.name, newItems);
                  }}
                  className="flex-1"
                />
                <button onClick={() => onChange(field.name, items.filter((_, i) => i !== idx))} className="p-2 text-red-500 hover:bg-red-50 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => onChange(field.name, [...items, ''])}>
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          </div>
        );
      }

      default:
        return (
          <Input value={value || ''} onChange={(e) => onChange(field.name, e.target.value)} />
        );
    }
  };

  return (
    <div className="space-y-1">
      <Label className="flex items-center gap-1">
        {field.label}
        {field.required && <span className="text-red-500">*</span>}
      </Label>
      {renderField()}
      {field.max_length && (
        <p className="text-xs text-gray-400">{(value || '').length}/{field.max_length}</p>
      )}
    </div>
  );
};

export default FieldEditor;
